import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ChevronDown, MessageCircle } from "lucide-react";
import { LOGO_URL } from "../data/projects";
import { buildWhatsappUrl, PHONE_TEL } from "../lib/whatsapp";

const navItems = [
 {
  to: "/about",
  label: "About",
  children: [    
    { to: "/about#careers", label: "Careers" },
    { to: "/about#founder", label: "Founder" },
  ],
},
  {
    to: "/projects",
    label: "Projects",
    
  },
  { to: "/gallery", label: "Gallery" },
  
  { to: "/contact", label: "Contact" },
];

// Mobile menu uses a subset of the top-level items per design brief.
const mobileNavItems = [
  {
  to: "/about",
  label: "About",
  children: [    
    { to: "/about#careers", label: "Careers" },
    { to: "/about#founder", label: "Founder" },
  ],
},
  {
    to: "/projects",
    label: "Projects",
    
  },
  { to: "/gallery", label: "Gallery" },
  
  { to: "/contact", label: "Contact" },
];


export default function Header({ onEnquire }) {
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState("Projects"); // expand projects by default
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
  if (location.hash) {
    const id = location.hash;

    setTimeout(() => {
      const el = document.querySelector(id);

      if (el) {
        const yOffset = -100; // adjust based on header height
        const y =
          el.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;

        window.scrollTo({
          top: y,
          behavior: "smooth",
        });
      }
    }, 300); // wait for page render
  }
}, [location]);

  useEffect(() => {
    setOpen(false);
    setDropdownOpen(null);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const waUrl = buildWhatsappUrl("Hello, I'd like to know more about Sanggavai Constructions.");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "backdrop-blur-md bg-white/40 border-b border-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12 max-w-7xl flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Sanggavai's" className="h-11 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((n) =>
            n.children ? (
              <div
                key={n.to}
                className="relative"
                onMouseEnter={() => setDropdownOpen(n.label)}
                onMouseLeave={() => setDropdownOpen(null)}
              >
                <NavLink
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `nav-link inline-flex items-center gap-1 text-[15px] font-medium tracking-wide ${
                      isActive ? "text-[#7A2E5D] active" : "text-[#1A1A1A]"
                    }`
                  }
                >
                  {n.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${dropdownOpen === n.label ? "rotate-180" : ""}`} />
                </NavLink>
                {dropdownOpen === n.label && (
                  <div data-testid="projects-dropdown" className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                    <div className="min-w-[220px] bg-white rounded-xl border border-black/5 shadow-xl overflow-hidden">
                      {n.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`dropdown-${c.to.split("/").pop()}`}
                          className={({ isActive }) =>
                            `block px-5 py-2 text-[14px] transition-colors ${
                              isActive
                                ? "bg-[#FAFAFA] text-[#7A2E5D]"
                                : "text-[#1A1A1A] hover:bg-[#FAFAFA] hover:text-[#7A2E5D]"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `nav-link text-[15px] font-medium tracking-wide ${
                    isActive ? "text-[#7A2E5D] active" : "text-[#1A1A1A]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="header-call-btn"
            className="inline-flex items-center gap-2 border-2 border-[#7A2E5D] text-[#7A2E5D] rounded-full px-5 py-2.5 font-medium hover:bg-[#7A2E5D] hover:text-white transition-all duration-300"
          >
            <Phone className="h-4 w-4" /> Call
          </a>
          <button
            type="button"
            onClick={onEnquire}
            data-testid="header-enquire-btn"
            className="brand-gradient text-white rounded-full px-6 py-2.5 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
          >
            Enquire Now
          </button>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 text-[#1A1A1A]"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* MOBILE SLIDE-IN SIDEBAR — rendered via portal so fixed positioning
          is relative to the viewport (not the header, which uses backdrop-blur
          and would otherwise create a containing block). */}
      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              {/* Backdrop */}
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-backdrop"
                className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm lg:hidden"
                aria-hidden
              />
              {/* Panel */}
              <motion.aside
                key="panel"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "tween", ease: [0.2, 0.8, 0.2, 1], duration: 0.35 }}
                data-testid="mobile-menu-overlay"
                className="fixed top-0 right-0 bottom-0 z-[70] w-[86%] max-w-sm bg-white shadow-2xl flex flex-col lg:hidden"
                role="dialog"
                aria-modal="true"
              >
                {/* Top row: logo + close */}
                <div className="flex items-center justify-between px-6 h-20 border-b border-black/5">
                  <img src={LOGO_URL} alt="Sanggavai's" className="h-10 w-auto" />
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-menu-close"
                    aria-label="Close menu"
                    className="h-10 w-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FAFAFA] transition-colors"
                  >
                    <X className="h-5 w-5 text-[#1A1A1A]" />
                  </button>
                </div>

                {/* Nav links */}
                <nav className="flex-1 overflow-y-auto px-6 py-8">
                  <ul className="flex flex-col gap-1">
                    {mobileNavItems.map((n) =>
                      n.children ? (
                        <li key={n.to}>
                          <button
                            type="button"
                            onClick={() => setMobileSub(mobileSub === n.label ? null : n.label)}
                            data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                            className={`w-full flex items-center justify-between py-4 text-lg font-semibold tracking-wide transition-colors ${
                              location.pathname.startsWith(n.to) ? "text-[#7A2E5D]" : "text-[#1A1A1A]"
                            }`}
                          >
                            <span className="flex items-center gap-3">
                              {n.label}
                              {location.pathname.startsWith(n.to) && (
                                <span className="h-1.5 w-1.5 rounded-full brand-gradient" />
                              )}
                            </span>
                            <ChevronDown
                              className={`h-5 w-5 text-[#555] transition-transform duration-300 ${
                                mobileSub === n.label ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                          <AnimatePresence initial={false}>
                            {mobileSub === n.label && (
                              <motion.div
                                key="sub"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                                className="overflow-hidden"
                              >
                                <ul className="pl-5 ml-1 border-l-2 border-[#7A2E5D]/20 my-2 space-y-1">
                                  {n.children.map((c) => (
                                    <li key={c.to}>
                                      <NavLink
                                        to={c.to}
                                        data-testid={`mobile-dropdown-${c.to.split("/").pop()}`}
                                        className={({ isActive }) =>
                                          `block py-3 text-base transition-colors ${
                                            isActive
                                              ? "text-[#7A2E5D] font-semibold"
                                              : "text-[#555] hover:text-[#7A2E5D]"
                                          }`
                                        }
                                      >
                                        {c.label}
                                      </NavLink>
                                    </li>
                                  ))}
                                </ul>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </li>
                      ) : (
                        <li key={n.to}>
                          <NavLink
                            to={n.to}
                            data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                              `flex items-center gap-3 py-4 text-lg font-semibold tracking-wide transition-colors ${
                                isActive ? "text-[#7A2E5D]" : "text-[#1A1A1A] hover:text-[#7A2E5D]"
                              }`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <span>{n.label}</span>
                                {isActive && <span className="h-1.5 w-1.5 rounded-full brand-gradient" />}
                              </>
                            )}
                          </NavLink>
                        </li>
                      )
                    )}
                  </ul>

                  {/* Enquire Now highlight button */}
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onEnquire && onEnquire();
                    }}
                    data-testid="mobile-enquire-btn"
                    className="brand-gradient text-white rounded-full w-full px-6 py-4 mt-8 font-medium text-base shadow-lg hover:shadow-xl active:scale-[0.99] transition-all"
                  >
                    Enquire Now
                  </button>
                </nav>

                {/* Sticky bottom Call + WhatsApp */}
                <div
                  className="border-t border-black/5 bg-white px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex gap-3"
                  data-testid="mobile-menu-footer"
                >
                  <a
                    href={`tel:${PHONE_TEL}`}
                    data-testid="mobile-call-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#7A2E5D] text-[#7A2E5D] rounded-full px-4 py-3 font-medium text-sm hover:bg-[#7A2E5D] hover:text-white transition-all"
                  >
                    <Phone className="h-4 w-4" /> Call
                  </a>
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-whatsapp-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full px-4 py-3 font-medium text-sm hover:-translate-y-0.5 hover:shadow-md transition-all"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
